'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  BookOpen,
  FlaskRound,
  PencilRuler,
  Computer,
  Palette,
  Volleyball
} from 'lucide-react';

interface Subject {
  id: string;
  title: string;
  image: string;
  icon: any;
  color: string;
  href: string; // Added href property for linking to subject pages
}

const CourseSection = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);

  useEffect(() => {
    setSubjects([
      {
        id: '1',
        title: 'Mathematics',
        image: '/images/maths.jpg',
        icon: BookOpen,
        color: 'from-blue-400 via-blue-500 to-blue-700',
        href: '/courses/mathematics'
      },
      {
        id: '2',
        title: 'Science',
        image: '/images/Science.jpg',
        icon: FlaskRound,
        color: 'from-green-400 via-green-500 to-green-700',
        href: '/courses/science'
      },
      {
        id: '3',
        title: 'English',
        image: '/images/English.jpg',
        icon: PencilRuler,
        color: 'from-yellow-300 via-yellow-400 to-yellow-600',
        href: '/courses/english'
      },
      {
        id: '4',
        title: 'Computer Studies',
        image: '/images/Computer.jpg',
        icon: Computer,
        color: 'from-purple-400 via-purple-500 to-purple-700',
        href: '/courses/computer-studies'
      },
      {
        id: '5',
        title: 'Art & Craft',
        image: '/images/Art & Craft.jpg',
        icon: Palette,
        color: 'from-pink-400 via-pink-500 to-pink-700',
        href: '/courses/art-and-craft'
      },
      {
        id: '6',
        title: 'Physical Education',
        image: '/images/Physical.jpg',
        icon: Volleyball,
        color: 'from-orange-400 via-orange-500 to-orange-700',
        href: '/courses/physical-education'
      }
    ]);
  }, []);

  return (
    <section data-component-id="src/app/CourseSection.tsx:82:4" data-component-path="src/app/CourseSection.tsx" data-component-line="82" data-component-file="CourseSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22w-full%20mt-%5B80px%5D%20sm%3Amt-%5B120px%5D%22%7D" className="w-full mt-[80px] sm:mt-[120px]">
      <div data-component-id="src/app/CourseSection.tsx:83:6" data-component-path="src/app/CourseSection.tsx" data-component-line="83" data-component-file="CourseSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20max-w-%5B1440px%5D%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* HEADING */}
        <motion.div data-component-id="src/app/CourseSection.tsx:86:8" data-component-path="src/app/CourseSection.tsx" data-component-line="86" data-component-file="CourseSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%22%7D"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="flex flex-col items-center"
        >
          <h2 data-component-id="src/app/CourseSection.tsx:93:10" data-component-path="src/app/CourseSection.tsx" data-component-line="93" data-component-file="CourseSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-%5B22px%5D%20sm%3Atext-%5B40px%5D%20font-extrabold%20text-center%20text-gray-800%20drop-shadow-sm%22%2C%22textContent%22%3A%22Explore%20Our%20Subjects%22%7D" className="text-[22px] sm:text-[40px] font-extrabold text-center text-gray-800 drop-shadow-sm">
            Explore Our Subjects
          </h2>

          <motion.p data-component-id="src/app/CourseSection.tsx:97:10" data-component-path="src/app/CourseSection.tsx" data-component-line="97" data-component-file="CourseSection.tsx" data-component-name="motion.p" data-component-content="%7B%22elementName%22%3A%22motion.p%22%2C%22className%22%3A%22text-%5B10px%5D%20sm%3Atext-%5B17px%5D%20text-center%20text-gray-600%20mt-3%20max-w-%5B420px%5D%22%2C%22textContent%22%3A%22A%20colorful%20learning%20journey%20for%20every%20child%20helping%20them%20discover%20their%20passion.%22%7D"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-[10px] sm:text-[17px] text-center text-gray-600 mt-3 max-w-[420px]"
          >
            A colorful learning journey for every child helping them discover their passion.
          </motion.p>
        </motion.div>

        {/* SUBJECT CARDS - Two rows with 3 images each */}
        <div data-component-id="src/app/CourseSection.tsx:108:8" data-component-path="src/app/CourseSection.tsx" data-component-line="108" data-component-file="CourseSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20gap-6%20sm%3Agap-10%20mt-10%20sm%3Amt-16%22%7D" className="flex flex-col gap-6 sm:gap-10 mt-10 sm:mt-16">
          {/* First Row */}
          <div data-component-id="src/app/CourseSection.tsx:110:10" data-component-path="src/app/CourseSection.tsx" data-component-line="110" data-component-file="CourseSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-3%20gap-6%20sm%3Agap-10%22%7D" className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-10">
            {subjects.slice(0, 3).map((subj, index) => {
              const Icon = subj.icon;

              return (
                <Link data-component-id="src/app/CourseSection.tsx:115:16" data-component-path="src/app/CourseSection.tsx" data-component-line="115" data-component-file="CourseSection.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22cursor-pointer%20group%22%7D" key={subj.id} href={subj.href} className="cursor-pointer group">
                  <motion.div data-component-id="src/app/CourseSection.tsx:116:18" data-component-path="src/app/CourseSection.tsx" data-component-line="116" data-component-file="CourseSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22h-full%22%7D"
                    initial={{ opacity: 0, x: -40, scale: 0.9 }}
                    whileInView={{ opacity: 1, x: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15, duration: 0.7 }}
                    whileHover={{ scale: 1.07 }}
                    className="h-full"
                  >
                    {/* CARD WRAPPER WITH GRADIENT BORDER */}
                    <div data-component-id="src/app/CourseSection.tsx:125:20" data-component-path="src/app/CourseSection.tsx" data-component-line="125" data-component-file="CourseSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20relative%20rounded-2xl%20overflow-hidden%20shadow-xl%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20border-%5B3px%5D%20border-transparent%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20bg-gradient-to-br%20from-transparent%20to-transparent%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20hover%3Aborder-orange-400%20hover%3Ashadow-%5B0_0_25px_rgba(255%2C140%2C0%2C0.45)%5D%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20transition-all%20duration-500%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20h-full%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%22%7D" className="
                      relative rounded-2xl overflow-hidden shadow-xl 
                      border-[3px] border-transparent 
                      bg-gradient-to-br from-transparent to-transparent
                      hover:border-orange-400 hover:shadow-[0_0_25px_rgba(255,140,0,0.45)]
                      transition-all duration-500
                      h-full
                    ">

                      {/* FULL IMAGE FIT + PARALLAX */}
                      <motion.div data-component-id="src/app/CourseSection.tsx:135:22" data-component-path="src/app/CourseSection.tsx" data-component-line="135" data-component-file="CourseSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22w-full%20h-%5B140px%5D%20sm%3Ah-%5B210px%5D%20relative%22%7D"
                        className="w-full h-[140px] sm:h-[210px] relative"
                        whileHover={{ scale: 1.14 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Image data-component-id="src/app/CourseSection.tsx:140:24" data-component-path="src/app/CourseSection.tsx" data-component-line="140" data-component-file="CourseSection.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-cover%22%7D"
                          src={subj.image}
                          alt={subj.title}
                          fill
                          className="object-cover"
                          unoptimized={true}
                        />
                      </motion.div>

                      {/* COOL OVERLAY */}
                      <motion.div data-component-id="src/app/CourseSection.tsx:150:22" data-component-path="src/app/CourseSection.tsx" data-component-line="150" data-component-file="CourseSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20absolute%20inset-0%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20bg-gradient-to-t%20from-black%2F90%20via-black%2F30%20to-transparent%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20flex%20flex-col%20justify-end%20p-4%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%22%7D"
                        className="
                          absolute inset-0 
                          bg-gradient-to-t from-black/90 via-black/30 to-transparent
                          flex flex-col justify-end p-4
                        "
                        initial={{ opacity: 0.7 }}
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.4 }}
                      >
                        {/* ICON WITH GLOW */}
                        <motion.div data-component-id="src/app/CourseSection.tsx:161:24" data-component-path="src/app/CourseSection.tsx" data-component-line="161" data-component-file="CourseSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%7D"
                          whileHover={{ scale: 1.25, rotate: 6 }}
                          animate={{ y: [0, -4, 0] }}
                          transition={{ repeat: Infinity, duration: 2 }}
                          className={`
                            w-12 h-12 sm:w-14 sm:h-14
                            rounded-xl flex items-center justify-center
                            text-white shadow-xl
                            bg-gradient-to-br ${subj.color}
                            ring-2 ring-white/40
                          `}
                        >
                          <Icon data-component-id="src/app/CourseSection.tsx:173:26" data-component-path="src/app/CourseSection.tsx" data-component-line="173" data-component-file="CourseSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22w-7%20h-7%20drop-shadow-md%22%7D" className="w-7 h-7 drop-shadow-md" />
                        </motion.div>

                        {/* SUBJECT TITLE */}
                        <motion.h3 data-component-id="src/app/CourseSection.tsx:177:24" data-component-path="src/app/CourseSection.tsx" data-component-line="177" data-component-file="CourseSection.tsx" data-component-name="motion.h3" data-component-content="%7B%22elementName%22%3A%22motion.h3%22%2C%22className%22%3A%22text-white%20text-%5B13px%5D%20sm%3Atext-%5B20px%5D%20font-bold%20mt-3%20drop-shadow-lg%22%7D"
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6 }}
                          className="text-white text-[13px] sm:text-[20px] font-bold mt-3 drop-shadow-lg"
                        >
                          {subj.title}
                        </motion.h3>
                      </motion.div>

                    </div>
                  </motion.div>
                </Link>
              );
            })}
          </div>

          {/* Second Row */}
          <div data-component-id="src/app/CourseSection.tsx:195:10" data-component-path="src/app/CourseSection.tsx" data-component-line="195" data-component-file="CourseSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-3%20gap-6%20sm%3Agap-10%22%7D" className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-10">
            {subjects.slice(3, 6).map((subj, index) => {
              const Icon = subj.icon;

              return (
                <Link data-component-id="src/app/CourseSection.tsx:200:16" data-component-path="src/app/CourseSection.tsx" data-component-line="200" data-component-file="CourseSection.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22cursor-pointer%20group%22%7D" key={subj.id} href={subj.href} className="cursor-pointer group">
                  <motion.div data-component-id="src/app/CourseSection.tsx:201:18" data-component-path="src/app/CourseSection.tsx" data-component-line="201" data-component-file="CourseSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22h-full%22%7D"
                    initial={{ opacity: 0, x: -40, scale: 0.9 }}
                    whileInView={{ opacity: 1, x: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15, duration: 0.7 }}
                    whileHover={{ scale: 1.07 }}
                    className="h-full"
                  >
                    {/* CARD WRAPPER WITH GRADIENT BORDER */}
                    <div data-component-id="src/app/CourseSection.tsx:210:20" data-component-path="src/app/CourseSection.tsx" data-component-line="210" data-component-file="CourseSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20relative%20rounded-2xl%20overflow-hidden%20shadow-xl%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20border-%5B3px%5D%20border-transparent%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20bg-gradient-to-br%20from-transparent%20to-transparent%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20hover%3Aborder-orange-400%20hover%3Ashadow-%5B0_0_25px_rgba(255%2C140%2C0%2C0.45)%5D%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20transition-all%20duration-500%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20h-full%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%22%7D" className="
                      relative rounded-2xl overflow-hidden shadow-xl 
                      border-[3px] border-transparent 
                      bg-gradient-to-br from-transparent to-transparent
                      hover:border-orange-400 hover:shadow-[0_0_25px_rgba(255,140,0,0.45)]
                      transition-all duration-500
                      h-full
                    ">

                      {/* FULL IMAGE FIT + PARALLAX */}
                      <motion.div data-component-id="src/app/CourseSection.tsx:220:22" data-component-path="src/app/CourseSection.tsx" data-component-line="220" data-component-file="CourseSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22w-full%20h-%5B140px%5D%20sm%3Ah-%5B210px%5D%20relative%22%7D"
                        className="w-full h-[140px] sm:h-[210px] relative"
                        whileHover={{ scale: 1.14 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Image data-component-id="src/app/CourseSection.tsx:225:24" data-component-path="src/app/CourseSection.tsx" data-component-line="225" data-component-file="CourseSection.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-cover%22%7D"
                          src={subj.image}
                          alt={subj.title}
                          fill
                          className="object-cover"
                          unoptimized={true}
                        />
                      </motion.div>

                      {/* COOL OVERLAY */}
                      <motion.div data-component-id="src/app/CourseSection.tsx:235:22" data-component-path="src/app/CourseSection.tsx" data-component-line="235" data-component-file="CourseSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20absolute%20inset-0%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20bg-gradient-to-t%20from-black%2F90%20via-black%2F30%20to-transparent%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20flex%20flex-col%20justify-end%20p-4%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%22%7D"
                        className="
                          absolute inset-0 
                          bg-gradient-to-t from-black/90 via-black/30 to-transparent
                          flex flex-col justify-end p-4
                        "
                        initial={{ opacity: 0.7 }}
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.4 }}
                      >
                        {/* ICON WITH GLOW */}
                        <motion.div data-component-id="src/app/CourseSection.tsx:246:24" data-component-path="src/app/CourseSection.tsx" data-component-line="246" data-component-file="CourseSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%7D"
                          whileHover={{ scale: 1.25, rotate: 6 }}
                          animate={{ y: [0, -4, 0] }}
                          transition={{ repeat: Infinity, duration: 2 }}
                          className={`
                            w-12 h-12 sm:w-14 sm:h-14
                            rounded-xl flex items-center justify-center
                            text-white shadow-xl
                            bg-gradient-to-br ${subj.color}
                            ring-2 ring-white/40
                          `}
                        >
                          <Icon data-component-id="src/app/CourseSection.tsx:258:26" data-component-path="src/app/CourseSection.tsx" data-component-line="258" data-component-file="CourseSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22w-7%20h-7%20drop-shadow-md%22%7D" className="w-7 h-7 drop-shadow-md" />
                        </motion.div>

                        {/* SUBJECT TITLE */}
                        <motion.h3 data-component-id="src/app/CourseSection.tsx:262:24" data-component-path="src/app/CourseSection.tsx" data-component-line="262" data-component-file="CourseSection.tsx" data-component-name="motion.h3" data-component-content="%7B%22elementName%22%3A%22motion.h3%22%2C%22className%22%3A%22text-white%20text-%5B13px%5D%20sm%3Atext-%5B20px%5D%20font-bold%20mt-3%20drop-shadow-lg%22%7D"
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6 }}
                          className="text-white text-[13px] sm:text-[20px] font-bold mt-3 drop-shadow-lg"
                        >
                          {subj.title}
                        </motion.h3>
                      </motion.div>

                    </div>
                  </motion.div>
                </Link>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};

export default CourseSection;